import { useState, useEffect, useRef } from "react";

interface CombinedShapeMaskProps {
  children: React.ReactNode;
  splitPointPercent?: number;
  controlPointYPercent?: number;
  curveFactor?: number;
  disableOnDesktop?: boolean;
  backgroundColor?: string;
}

const CombinedShapeMask = ({ 
  children, 
  splitPointPercent = 70,   // Percentage of container height where curve begins
  controlPointYPercent = 90, // Percentage of container height for control points
  curveFactor = 0.5,        // Controls horizontal position of control points (0.1 to 0.9)
  disableOnDesktop = true,  // Whether to disable the mask on desktop
  backgroundColor = "white" // Background color of the container
}: CombinedShapeMaskProps) => {
  const [isMobile, setIsMobile] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const [maskId] = useState(`curved-mask-${Math.random().toString(36).substring(2, 9)}`);

  useEffect(() => {
    // Check if we're on mobile and update dimensions
    const updateDimensions = () => {
      const isMobileView = window.innerWidth < 768; // md breakpoint in Tailwind
      setIsMobile(isMobileView);

      if (containerRef.current) {
        setDimensions({
          width: containerRef.current.offsetWidth,
          height: containerRef.current.offsetHeight
        });
      }
    };

    // Initial check
    updateDimensions();

    // Add event listener for window resize
    window.addEventListener('resize', updateDimensions);

    // Setup ResizeObserver for the container element
    const resizeObserver = new ResizeObserver(updateDimensions);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    // Cleanup
    return () => {
      window.removeEventListener('resize', updateDimensions);
      resizeObserver.disconnect();
    };
  }, []);

  // Show mask only on mobile if disableOnDesktop is true
  const showMask = !disableOnDesktop || isMobile;

  if (!showMask) {
    // If mask is disabled, just render children normally
    return <div ref={containerRef}>{children}</div>;
  }

  // If we have dimensions, render with SVG mask
  if (dimensions.width > 0 && dimensions.height > 0) {
    const { width, height } = dimensions;

    // Convert percentage values to actual pixel positions
    const splitPoint = Math.floor(height - ((splitPointPercent / 100) * height));
    const controlY = Math.floor((controlPointYPercent / 100) * height);

    // Calculate control points for the cubic bezier curve
    const x1 = width * curveFactor;        // First control point X
    const y1 = controlY;                   // First control point Y
    const x2 = width * (1 - curveFactor);  // Second control point X
    const y2 = controlY;                   // Second control point Y

    // Generate the path for the cubic bezier curve
    const path = `
      M0,${splitPoint} 
      C${x1},${y1} ${x2},${y2} ${width},${splitPoint} 
      V${height} H0 Z
    `;

    return (
      <div style={{ position: 'relative', width: '100%', height: '100%' }} ref={containerRef}>
        <svg 
          width={width} 
          height={height}
          viewBox={`0 0 ${width} ${height}`}
          style={{ 
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            zIndex: 1
          }}
        >
          <defs>
            <mask id={maskId}>
              <rect width="100%" height="99%" fill="white" />
              <path 
                d={path.trim()} 
                fill="black"
              />
            </mask>
          </defs>
          <foreignObject 
            width="100%" 
            height="100%" 
            mask={`url(#${maskId})`}
          >
            <div style={{ width: '100%', height: '100%' }}>
              {children}
            </div>
          </foreignObject>
        </svg>
      </div>
    );
  }

  // While dimensions are loading, render normally
  return (
    <div ref={containerRef} style={{ width: '100%', height: '100%' }}>
      {children}
    </div>
  );
};

export default CombinedShapeMask;